/**
 * Nachrichten — Mails wie ein Messenger, als Blatt von rechts (Benjamin, 23.09.2026).
 *
 * Links oben die Gespräche, neueste zuerst, ein Punkt für Neues. Ein Tipp öffnet
 * den Verlauf als Blasen mit Antwortfeld. Rechnungen von Lieferanten stehen hier
 * nicht — die gehören ins Belegregister. Am Telefon füllt das Blatt den Schirm.
 *
 * Seit 24.09.2026: Schließen versteckt das Blatt nur (`ist-zu`), es bleibt
 * stehen — beim nächsten Öffnen ist man wieder im selben Gespräch, mit
 * demselben Filter und Scrollstand. Und meldet die Seite einen Bezug (Beleg,
 * Projekt), stehen dessen Gespräche oben.
 */
import { useEffect, useRef, useState } from "react";

import { fehlerText } from "@vimaana/ui";

import { usePersonen } from "../../hooks/useNotizen";
import { useNachrichtenListe, type FadenKurz, type NachrichtFilter } from "../../hooks/useNachrichten";
import { vorname } from "../../lib/notizen";
import { ART_LABEL, initialen, zeitKurz, zuordnungText } from "../../lib/nachrichten";
import FadenAnsicht from "./FadenAnsicht";
import { useNachrichtenFenster } from "./NachrichtenKontext";
import "../../styles/projekte.css";
import "../../styles/nachrichten.css";

const ARTEN: { id: NachrichtFilter; label: string }[] = [
  { id: "alle", label: "Alle" },
  { id: "kunde", label: "Kunden" },
  { id: "leed", label: "Leeds" },
  { id: "lieferant", label: "Lieferanten" },
];

export default function NachrichtenBlatt() {
  const { offen, ziel, zielNr, bezug, schliessen } = useNachrichtenFenster();
  const [jemalsOffen, setJemalsOffen] = useState(false);
  const rahmen = useRef<HTMLDivElement>(null);
  const personen = usePersonen().data || [];
  const [wer, setWer] = useState("meine");
  const [art, setArt] = useState<NachrichtFilter>("alle");
  const [such, setSuch] = useState("");
  const [kunde, setKunde] = useState<{ id: string; name: string } | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string>("");

  useEffect(() => { if (offen) setJemalsOffen(true); }, [offen]);

  // Geschlossen ist das Blatt nur versteckt — für Tastatur und Vorleser aber weg.
  useEffect(() => { rahmen.current?.toggleAttribute("inert", !offen); }, [offen, jemalsOffen]);

  // Nur ein Öffnen MIT Ziel (ein Kunde, ein Gespräch) setzt den Stand neu;
  // ein schlichtes Öffnen macht dort weiter, wo man aufgehört hat.
  useEffect(() => {
    if (!zielNr) return;
    setKunde(ziel.kundeId ? { id: ziel.kundeId, name: ziel.kundeName || "Kunde" } : null);
    setGewaehlt(ziel.schluessel || "");
    if (ziel.kundeId) { setWer("alle"); setArt("alle"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zielNr]);

  useEffect(() => {
    if (!offen) return;
    const zu = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || document.querySelector(".vg-schreib-schleier, .nb-dialog, .nb-oben-rahmen .k-blatt, .vg-doklight, .invoice-pflege-backdrop")) return;
      if (gewaehlt) setGewaehlt(""); else schliessen();
    };
    window.addEventListener("keydown", zu);
    return () => window.removeEventListener("keydown", zu);
  }, [offen, gewaehlt, schliessen]);

  const liste = useNachrichtenListe({ wer, art, kundeId: kunde?.id, q: such.trim() || undefined }, offen);
  // Der Bezug der Seite: alle Gespräche dieses Kunden, egal wessen Postfach und
  // welcher Filter gerade steht — nur nicht, wenn ohnehin auf einen Kunden
  // gefiltert oder gesucht wird.
  const mitBezug = !!bezug?.kundeId && !kunde && !such.trim();
  const bezugListe = useNachrichtenListe({ wer: "alle", art: "alle", kundeId: bezug?.kundeId }, offen && mitBezug);

  if (!jemalsOffen) return null;

  const werFilter = [
    { id: "meine", label: "Meine" },
    ...personen.filter((p) => !p.ich).map((p) => ({ id: String(p.id), label: vorname(p.name) })),
    { id: "alle", label: "Alle" },
  ];
  const alleFaeden = liste.data?.faeden || [];
  const neu = liste.data?.neu;
  // Oben: die Gespräche des Kunden aus dem Bezug, dazu alles, was am Projekt
  // hängt (etwa ein Lieferantenangebot) — die zum Projekt zuerst.
  const oben: FadenKurz[] = [];
  if (bezug && !kunde && !such.trim()) {
    const kandidaten = [
      ...(mitBezug ? bezugListe.data?.faeden || [] : []),
      ...alleFaeden.filter((f) => bezug.projektId && f.projekt_id === bezug.projektId),
    ];
    for (const f of kandidaten) if (!oben.some((o) => o.schluessel === f.schluessel)) oben.push(f);
    oben.sort((a, b) => Number(b.projekt_id === bezug.projektId && !!bezug.projektId) - Number(a.projekt_id === bezug.projektId && !!bezug.projektId));
  }
  const faeden = alleFaeden.filter((f) => !oben.some((o) => o.schluessel === f.schluessel));

  return (
    <div ref={rahmen} className={"nb" + (offen ? "" : " ist-zu")} role="dialog" aria-modal="true" aria-label="Nachrichten" aria-hidden={!offen}>
      <button type="button" className="nb-schleier" aria-label="Schließen" onClick={schliessen} />
      <aside className="nb-panel projekte-page">
        {gewaehlt ? (
          <FadenAnsicht schluessel={gewaehlt} onZurueck={() => setGewaehlt("")} onSchliessen={schliessen} sichtbar={offen} onWechseln={setGewaehlt} />
        ) : (
          <>
            <header className="nb-kopf">
              <h2>Nachrichten</h2>
              <button type="button" className="nb-zu" aria-label="Schließen" onClick={schliessen}>×</button>
            </header>
            <div className="nb-filter">
              {kunde ? (
                <p className="nb-kundenfilter">
                  <span>Nur <strong>{kunde.name}</strong></span>
                  <button type="button" className="vg-post-link" onClick={() => setKunde(null)}>alle zeigen</button>
                </p>
              ) : (
                <>
                  <div className="nz-segmente" role="group" aria-label="Wessen Postfach">
                    {werFilter.map((f) => (
                      <button key={f.id} type="button" className={"nz-seg" + (wer === f.id ? " is-aktiv" : "")} aria-pressed={wer === f.id} onClick={() => setWer(f.id)}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <div className="nb-arten" role="group" aria-label="Art">
                    {ARTEN.map((a) => (
                      <button key={a.id} type="button" className={"vg-chip-knopf" + (art === a.id ? " is-active" : "")} aria-pressed={art === a.id} onClick={() => setArt(a.id)}>
                        {a.label}
                        {!!neu?.[a.id] && <em className="nb-zahl" aria-label={`${neu[a.id]} neu`}>{neu[a.id]}</em>}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <input
                className="nb-suche" type="search" value={such} onChange={(e) => setSuch(e.target.value)}
                placeholder="Suchen: Name, Betreff, Projekt" aria-label="Nachrichten durchsuchen"
              />
            </div>

            <div className="nb-liste-rahmen">
              {liste.isLoading && <p className="nb-leer">Lade …</p>}
              {liste.error && <p className="vg-post-fehler nb-leer">{fehlerText(liste.error)}</p>}
              {oben.length > 0 && (
                <>
                  <h3 className="nb-abschnitt">
                    Zu {bezug?.kundeName || bezug?.projektTitel || "diesem Beleg"}
                  </h3>
                  <ul className="nb-liste nb-liste-bezug">
                    {oben.map((f) => <Zeile key={f.schluessel} f={f} onOeffnen={() => setGewaehlt(f.schluessel)} />)}
                  </ul>
                  {faeden.length > 0 && <h3 className="nb-abschnitt">Alle Nachrichten</h3>}
                </>
              )}
              {!liste.isLoading && !liste.error && faeden.length === 0 && oben.length === 0 && (
                <p className="nb-leer">{such.trim() ? "Nichts gefunden." : "Keine Nachrichten in den letzten Monaten."}</p>
              )}
              <ul className="nb-liste">
                {faeden.map((f) => <Zeile key={f.schluessel} f={f} onOeffnen={() => setGewaehlt(f.schluessel)} />)}
              </ul>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function Zeile({ f, onOeffnen }: { f: FadenKurz; onOeffnen: () => void }) {
  const ohneProjekt = f.art !== "leed" && !f.projekt_titel;
  return (
    <li>
      <button type="button" className={"nb-zeile" + (f.neu ? " ist-neu" : "")} onClick={onOeffnen}>
        <span className={`nb-bild ist-${f.art}`} aria-hidden="true">{initialen(f.titel)}</span>
        <span className="nb-zeile-text">
          <span className="nb-zeile-oben">
            <strong className="nb-name">{f.titel}</strong>
            <time className="nb-zeit" dateTime={f.letzte}>{zeitKurz(f.letzte)}</time>
          </span>
          <span className="nb-vorschau">
            {f.letzte_von_uns && <span className="nb-du">Wir: </span>}
            {f.vorschau || f.betreff || "—"}
          </span>
          <span className="nb-zeile-unten">
            {f.art !== "kunde" && <span className={`nb-art ist-${f.art}`}>{ART_LABEL[f.art]}</span>}
            <span className={"nb-projekt" + (ohneProjekt ? " ist-offen" : "")}>{zuordnungText(f)}</span>
            {f.neu > 0 && <em className="nb-zahl" aria-label={`${f.neu} neu`}>{f.neu}</em>}
          </span>
        </span>
      </button>
    </li>
  );
}
